import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { getCurrentScrollPercentage, getViewArea } from '@/utils/scroll';
import { useThrottleFn } from 'ahooks';
import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

interface IProps {
  banner?: any;
  page_name?: string;
}
export default function usePageTrack({ banner, page_name = 'PG专题页B版' }: IProps) {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();

  const enterTimer = useRef<number>(Date.now());
  // 记录最后的滚动状态，避免页面跳转时滚动位置被重置
  const lastScrollState = useRef({ viewAdvance: 0, viewArea: 1 });

  const trackPageView = useCallback(() => {
    const { refer_page_name, refer_url_path } = getFromRouterInfo();
    enterTimer.current = Date.now();

    trackEvent(
      {
        eventName: 'PGPage_View',
        properties: {
          page_name,
          refer_page_name,
          refer_url_path,
        },
      },
      [ProviderType.SENSORS],
    );
  }, [trackEvent]);
  const trackPageLeave = useCallback(() => {
    // 使用保存的滚动状态，避免页面跳转时滚动位置被重置的问题
    const { viewAdvance } = lastScrollState.current;
    const { refer_page_name, refer_url_path } = getFromRouterInfo();
    trackEvent(
      {
        eventName: 'PGPage_Leave',
        properties: {
          page_name,
          refer_page_name,
          refer_url_path,
          view_advance: viewAdvance,
          view_duration: Math.ceil((Date.now() - enterTimer.current) / 1000),
        },
      },
      [ProviderType.SENSORS],
    );
  }, [trackEvent]);

  const [bannerRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });
  const [productRef, productInView] = useInView({
    threshold: 0,
    triggerOnce: true,
  });
  const [productBackRef, productBackInView] = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  // * banner埋点
  /**
   * 神策埋点：镜片专题页 Banner 曝光/点击
   * 对应Vue版本中 Banner 组件的 SDPageMkExp 函数
   * @TrackType sensorsdata
   * @EventType PGPageBanner_Exp | PGPageBanner_Click
   * @EventDesc pg专题页 Banner 曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   mkt_name: 营销位名称 (固定为 'banner'),
   *   group_id: 分组ID,
   * }
   * @param group_id 分组ID (可选)
   */
  const trackBanner = useCallback(
    (type: 'exp' | 'click', { is_video, pits_jump_type, pits_jump_content }) => {
      const eventName = type === 'exp' ? 'PGPageBanner_Exp' : 'PGPageBanner_Click';
      try {
        // 获取当前页面的中文名称
        const properties = {
          page_name,
          module_name: 'Banner位',
          pits_index: 0,
          is_video,
          pits_jump_type,
          pits_jump_content,
        };

        trackEvent(
          {
            eventName,
            properties,
          },
          [ProviderType.SENSORS],
        );

        logger.log(`🔍 神策埋点 - ${eventName}:`, properties);
      } catch (error) {
        logger.error(`${eventName} tracking error:`, error);
      }
    },
    [trackEvent],
  );
  useEffect(() => {
    if (inView) {
      trackBanner('exp', {
        is_video: banner.type === 1,
        pits_jump_type: `${banner?.jump_content?.jump_type}`,
        pits_jump_content: `${banner?.jump_content?.Jump_target}`,
      });
    }
  }, [inView]);
  const trackBannerClick = useCallback(() => {
    trackBanner('click', {
      is_video: banner.type === 1,
      pits_jump_type: `${banner?.jump_content?.jump_type}`,
      pits_jump_content: `${banner?.jump_content?.Jump_target}`,
    });
  }, [trackBanner]);

  // * 商品埋点
  const trackProduct = useCallback(
    (
      type: 'exp' | 'click',
      {
        module_name,
        pits_index,
        product_sku,
        business_sku,
        shop_sku,
        product_id,
        product_name,
        product_price,
        base_current_product_price,
        current_product_price,
        dollar_exchange_rate,
        currency,
      },
    ) => {
      trackEvent(
        {
          eventName: type === 'exp' ? 'PGPageProduct_Exp' : 'PGPageProduct_Click',
          properties: {
            page_name,
            module_name,
            pits_index,
            product_sku,
            business_sku,
            shop_sku,
            product_id,
            product_name,
            product_price,
            base_current_product_price,
            current_product_price,
            dollar_exchange_rate,
            currency,
          },
        },
        [ProviderType.SENSORS],
      );
    },
    [],
  );
  const trackPageMktClick = useCallback(({ module_name, pits_index = 0 }) => {
    trackEvent(
      {
        eventName: 'PGPageMkt_Click',
        properties: {
          page_name,
          module_name,
          pits_index,
        },
      },
      [ProviderType.SENSORS],
    );
  }, []);

  // 使用ahooks的useThrottleFn优化滚动事件性能
  const updateScrollState = useCallback(() => {
    try {
      const viewAdvance = getCurrentScrollPercentage();
      const viewArea = getViewArea();
      lastScrollState.current = { viewAdvance, viewArea };
    } catch (error) {
      // 忽略错误，使用默认值
      logger.error('Error updating scroll state:', error);
    }
  }, []);

  // 使用ahooks的节流Hook，100ms节流
  const { run: throttledUpdateScrollState } = useThrottleFn(updateScrollState, {
    wait: 100,
  });

  // 定期更新滚动状态，避免页面跳转时丢失滚动信息
  useEffect(() => {
    // 立即更新一次
    updateScrollState();

    // 监听滚动事件，使用ahooks节流处理
    const handleScroll = () => {
      throttledUpdateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [updateScrollState, throttledUpdateScrollState]);

  return {
    bannerRef,
    productRef,
    productInView,
    productBackRef,
    productBackInView,
    trackPageView,
    trackPageLeave,
    trackBannerClick,
    trackProduct,
    trackPageMktClick,
  };
}
